import React, { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  MapPin, 
  AlertTriangle, 
  Shield, 
  Clock, 
  Plus,
  Navigation,
  Loader2,
  Info,
  ChevronRight
} from "lucide-react";
import { MapContainer, TileLayer, Circle, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Risk level colors
const RISK_COLORS = {
  faible: { fill: "#FCD34D", stroke: "#F59E0B", bg: "bg-amber-100", text: "text-amber-700" },
  moyen: { fill: "#FB923C", stroke: "#EA580C", bg: "bg-orange-100", text: "text-orange-700" },
  eleve: { fill: "#F87171", stroke: "#DC2626", bg: "bg-red-100", text: "text-red-700" }
};

const RISK_LABELS = {
  faible: "Faible",
  moyen: "Moyen",
  eleve: "Élevé"
};

const CATEGORY_LABELS = {
  aggression: "Agression",
  vol: "Vol",
  harcelement: "Harcèlement",
  incivilite: "Incivilité",
  degradation: "Dégradation",
  evaluation_negative: "Évaluation négative",
  autre: "Autre"
};

// Component to recenter map
const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
};

const AlertsMapPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([45.7640, 4.8357]); // Lyon default
  const [selectedZone, setSelectedZone] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reportData, setReportData] = useState({
    category: "",
    description: "",
    severity: 2,
    latitude: null,
    longitude: null
  });

  // Get user location and fetch zones
  useEffect(() => {
    const init = async () => {
      // Get user location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const loc = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setUserLocation(loc);
            setMapCenter([loc.lat, loc.lng]);
            setReportData(prev => ({
              ...prev,
              latitude: loc.lat,
              longitude: loc.lng
            }));
            fetchZones(loc.lat, loc.lng);
          },
          () => {
            // Use default location (Lyon)
            fetchZones(45.7640, 4.8357);
          }
        );
      } else {
        fetchZones(45.7640, 4.8357);
      }
      
      // Fetch categories
      try {
        const response = await axios.get(`${API}/incidents/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchZones = async (lat, lng) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/alerts/zones`, {
        params: { lat, lng, radius_km: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(response.data.zones);
    } catch (error) {
      console.error("Error fetching zones:", error);
      toast.error("Erreur lors du chargement des alertes");
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    if (!reportData.category) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }
    
    if (!reportData.latitude || !reportData.longitude) {
      toast.error("Position non disponible");
      return;
    }
    
    setReportLoading(true);
    try {
      await axios.post(`${API}/incidents/report`, reportData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Signalement envoyé !");
      setShowReportDialog(false);
      setReportData({
        category: "",
        description: "",
        severity: 2,
        latitude: userLocation?.lat,
        longitude: userLocation?.lng
      });
      
      // Refresh zones
      if (userLocation) {
        fetchZones(userLocation.lat, userLocation.lng);
      }
    } catch (error) {
      toast.error("Erreur lors du signalement");
    } finally {
      setReportLoading(false);
    }
  };

  const centerOnUser = () => {
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setMapCenter([loc.lat, loc.lng]);
          fetchZones(loc.lat, loc.lng);
        },
        () => toast.error("Impossible d'obtenir votre position")
      );
    }
  };

  const getZoneMessage = (zone) => {
    const count = zone.incident_count;
    if (count === 1) {
      return "1 signalement récent dans ce secteur";
    }
    return `${count} signalements récents dans ce secteur`;
  };

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Carte des Alertes
            </h1>
            <p className="text-slate-500 text-sm">
              Zones avec signalements récents d'incivilités
            </p>
          </div>
          
          <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
            <DialogTrigger asChild>
              <Button className="btn-guardian rounded-full" data-testid="report-incident-btn">
                <Plus size={18} className="mr-2" />
                Signaler
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="text-orange-500" size={20} />
                  Signaler une incivilité
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="font-bold">Catégorie *</Label>
                  <Select
                    value={reportData.category}
                    onValueChange={(value) => setReportData({ ...reportData, category: value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-bold">Niveau de gravité</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 1, label: "Faible", color: "bg-amber-100 text-amber-700 border-amber-300" },
                      { value: 2, label: "Moyen", color: "bg-orange-100 text-orange-700 border-orange-300" },
                      { value: 3, label: "Élevé", color: "bg-red-100 text-red-700 border-red-300" }
                    ].map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => setReportData({ ...reportData, severity: level.value })}
                        className={`flex-1 py-2 px-3 rounded-xl border-2 font-medium text-sm transition-all ${
                          reportData.severity === level.value
                            ? level.color + " border-current"
                            : "bg-slate-50 text-slate-500 border-slate-200"
                        }`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-bold">Description (optionnel)</Label>
                  <Textarea
                    placeholder="Décrivez brièvement l'incident..."
                    value={reportData.description}
                    onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                    className="rounded-xl"
                    rows={3}
                  />
                </div>
                
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin size={16} className="text-[#FF3366]" />
                    {userLocation ? (
                      <span>Position actuelle utilisée</span>
                    ) : (
                      <span className="text-orange-600">Position non disponible</span>
                    )}
                  </div>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-xl">
                  <p className="text-xs text-blue-700">
                    <Info size={14} className="inline mr-1" />
                    Votre signalement est anonyme. Aucune information personnelle n'est partagée.
                  </p>
                </div>
                
                <Button
                  onClick={handleReport}
                  disabled={reportLoading || !reportData.category || !userLocation}
                  className="w-full btn-guardian rounded-xl"
                >
                  {reportLoading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Envoi...
                    </>
                  ) : (
                    "Envoyer le signalement"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Legend */}
        <Card className="guardian-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="font-medium text-slate-700">Niveaux de risque :</span>
              {Object.entries(RISK_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full opacity-70"
                    style={{ backgroundColor: RISK_COLORS[key].fill }}
                  />
                  <span className={RISK_COLORS[key].text}>{label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="guardian-card overflow-hidden" data-testid="alerts-map-card">
          <div className="relative h-[500px]">
            {loading && (
              <div className="absolute inset-0 bg-white/80 z-[1000] flex items-center justify-center">
                <Loader2 className="animate-spin text-[#FF3366]" size={40} />
              </div>
            )}
            
            <MapContainer
              center={mapCenter}
              zoom={13}
              className="h-full w-full"
              style={{ zIndex: 1 }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <MapController center={mapCenter} />
              
              {/* Alert Zones */}
              {zones.map((zone) => {
                const colors = RISK_COLORS[zone.risk_level] || RISK_COLORS.faible;
                return (
                  <Circle
                    key={zone.zone_id}
                    center={[zone.center_lat, zone.center_lng]}
                    radius={zone.radius}
                    pathOptions={{
                      color: colors.stroke,
                      fillColor: colors.fill,
                      fillOpacity: 0.4,
                      weight: 2
                    }}
                    eventHandlers={{
                      click: () => setSelectedZone(zone)
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold mb-2 ${colors.bg} ${colors.text}`}>
                          <AlertTriangle size={12} />
                          Risque {RISK_LABELS[zone.risk_level]}
                        </div>
                        <p className="font-medium text-slate-900 mb-1">
                          ⚠️ {getZoneMessage(zone)}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                          <Clock size={12} />
                          Dernier : {zone.last_incident}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {zone.categories.slice(0, 3).map((cat) => (
                            <span key={cat} className="px-2 py-0.5 bg-slate-100 rounded-full text-xs text-slate-600">
                              {CATEGORY_LABELS[cat] || cat}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Popup>
                  </Circle>
                );
              })}
              
              {/* User Location Marker */}
              {userLocation && (
                <Circle
                  center={[userLocation.lat, userLocation.lng]}
                  radius={50}
                  pathOptions={{
                    color: "#3B82F6",
                    fillColor: "#3B82F6",
                    fillOpacity: 0.5,
                    weight: 2
                  }}
                >
                  <Popup>Votre position</Popup>
                </Circle>
              )}
            </MapContainer>
            
            {/* Map Controls */}
            <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="rounded-full shadow-lg bg-white"
                onClick={centerOnUser}
              >
                <Navigation size={16} />
              </Button>
            </div>
          </div>
        </Card>

        {/* Zone List */}
        {zones.length > 0 && (
          <Card className="guardian-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="text-[#FF3366]" size={20} />
                Zones signalées ({zones.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {zones.slice(0, 5).map((zone) => {
                const colors = RISK_COLORS[zone.risk_level] || RISK_COLORS.faible;
                return (
                  <div
                    key={zone.zone_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => setMapCenter([zone.center_lat, zone.center_lng])}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colors.fill + "40" }}
                      >
                        <AlertTriangle size={20} style={{ color: colors.stroke }} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {zone.incident_count} signalement{zone.incident_count > 1 ? "s" : ""}
                        </p>
                        <p className="text-xs text-slate-500">
                          {zone.last_incident}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                        {RISK_LABELS[zone.risk_level]}
                      </span>
                      <ChevronRight size={16} className="text-slate-400" />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* No Zones Message */}
        {!loading && zones.length === 0 && (
          <Card className="guardian-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-green-600" size={32} />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Aucune alerte dans ce secteur</h3>
              <p className="text-slate-500 text-sm">
                Bonne nouvelle ! Il n'y a pas de signalement récent autour de votre position.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AlertsMapPage;
